import { get, writable } from 'svelte/store';
import { configService } from '$lib/services';

import { networkInformationStore } from '@components/room/no-internet-state/store';
import { roomStore } from '@components/room/store';
import { streamingDebuggerStore } from '@components/room/streaming-debugger/stores/debugger';
import type { StorageStrategy } from '@components/room/streaming-debugger/stores/logger/storage-strategy';
import {
    IndexedDBStrategy,
} from '@components/room/streaming-debugger/stores/logger/storage-strategy/index-db-strategy';
import { MemoryStrategy } from '@components/room/streaming-debugger/stores/logger/storage-strategy/memory-strategy';
import {
    type LogEntry,
    type LoggingState,
    type LoggingStore,
} from '@components/room/streaming-debugger/stores/logger/types';
import { isOperator, userStore } from '@components/user/store';

import { browser } from '$app/environment';

function LoggerStoreConstructor(): LoggingStore {
    const initialState: LoggingState = { logs: [], lastLog: null };
    const { subscribe, update, set } = writable<LoggingState>(initialState);

    let storageInitialized: StorageStrategy;

    function isIndexedDBAvailable(): boolean {
        try {
            return browser && !!window.indexedDB;
        } catch {
            return false;
        }
    }

    async function initializeMemoryStorage(): Promise<StorageStrategy> {
        const storage = new MemoryStrategy();
        await storage.init();
        return storage;
    }

    async function initializeIndexDBStorage(): Promise<StorageStrategy> {
        const storage = new IndexedDBStrategy();
        await storage.init();
        return storage;
    }

    async function initializeStorage():Promise<StorageStrategy> {
        if (!isIndexedDBAvailable()) {
            return await initializeMemoryStorage();
        }

        try {
            return await initializeIndexDBStorage();
        } catch (error) {
            console.warn('Falling back to in-memory storage:', error);
            return await initializeMemoryStorage();
        }
    }

    async function getStorage(): Promise<StorageStrategy> {
        if (!storageInitialized) {
            storageInitialized = await initializeStorage();
        }
        return storageInitialized;
    }

    const logEntryToString = (log: LogEntry) =>
        // eslint-disable-next-line max-len
        `[${new Date(log.timestamp).toISOString()}] [${log.level.toUpperCase()}] [isOperator: ${log.isOperator}] [roomId:${log.roomId?.toUpperCase()}] [${log.type.toUpperCase()}] -- ${log.message} -- ${JSON.stringify(log.extendedData)}`;

    async function getLogs() {
        const storage = await getStorage();
        return storage.getAll();
    }

    return {
        subscribe,
        log: async ({ type, message, level = 'info', roomId }, extendedData = {}, options = {}) => {
            const networkInfo = networkInformationStore.get();
            const connectionMetricsData = networkInformationStore.isSupported() ? {
                rtt: networkInfo.rtt,
                downlink: networkInfo.downlink,
                connectionEffectiveType: networkInfo.effectiveType,
                connectionType: networkInfo.type,
                isConnectionOnline: networkInfo.isOnline,
            } : {};

            const logEntry: LogEntry = {
                timestamp: new Date().getTime(),
                roomId: roomId || get(roomStore)?.activeRoom?.id,
                isOperator: get(isOperator),
                type,
                message,
                extendedData: JSON.stringify({ ...extendedData, ...connectionMetricsData  }),
                level,
                reportOptions: options,
            };
            if (get(streamingDebuggerStore) || configService.get('debug')) {
                console.log(logEntryToString(logEntry));
            }

            const storage = await getStorage();
            await storage.add(logEntry);

            update(state => ({
                logs: [...state.logs, logEntry],
                lastLog: logEntry,
            }));
        },
        clear: async () => {
            const storage = await getStorage();
            await storage.clear();
            set(initialState);
        },
        getLogs,
        downloadLogs: async () => {
            const logs = await getLogs();
            const logText = logs
                .map(log => logEntryToString(log))
                .join('\n \n');
            const blob = new Blob([logText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `streaming_logs_${get(userStore).user?.id}.txt`;

            a.click();
            URL.revokeObjectURL(url);
            console.log('Logs downloaded, count:', logs.length);
        },
        getLastLog: async (type: string): Promise<LogEntry | undefined> => {
            const storage = await getStorage();
            const logs = await storage.getAll();
            return logs.filter(log => log.type === type).pop();
        },
    };
}

export const streamingLoggerStore = LoggerStoreConstructor();
